import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSocial } from "@/lib/social";
import { Settings, Shield, Ban, Eye, CreditCard, User as UserIcon, LogOut, AlertTriangle, Check, Copy, VolumeX, ShieldCheck } from "lucide-react";
import KYCCard from "@/components/KYCCard";

const TABS = [
  { id: "profile", label: "Profile", icon: UserIcon },
  { id: "verify", label: "Streamer Verify", icon: ShieldCheck },
  { id: "security", label: "Security", icon: Shield },
  { id: "privacy", label: "Privacy", icon: Eye },
  { id: "blocks", label: "Blocks & Mutes", icon: Ban },
  { id: "plans", label: "Plans", icon: CreditCard },
  { id: "account", label: "Manage Account", icon: Settings },
];

export default function Account() {
  const { user, logout, refreshMe } = useAuth();
  const social = useSocial();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  const [form, setForm] = useState({ email: "", bio: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (!user) nav("/login"); }, [user, nav]);
  useEffect(() => { if (user) setForm({ email: user.email, bio: user.bio || "" }); }, [user]);

  if (!user) return null;

  const notify = (m) => { setMsg(m); setErr(""); setTimeout(() => setMsg(""), 2200); };
  const fail = (e) => { setErr(e); setMsg(""); setTimeout(() => setErr(""), 3000); };

  const saveProfile = async () => {
    try { await api.patch("/users/me", form); refreshMe(); notify("Profile saved"); }
    catch (e) { fail(e?.response?.data?.detail || "Save failed"); }
  };
  const savePassword = async () => {
    try { await api.post("/users/me/password", pwd); notify("Password updated"); setPwd({ current_password: "", new_password: "" }); }
    catch (e) { fail(e?.response?.data?.detail || "Change failed"); }
  };
  const toggleAnon = async () => {
    try { await api.patch("/users/me", { is_anonymous: !user.is_anonymous }); refreshMe(); notify(user.is_anonymous ? "Anonymous mode off" : "Anonymous mode on"); }
    catch (e) { fail("Failed"); }
  };
  const deleteAccount = async () => {
    if (!window.confirm("Permanently deactivate your account? This cannot be undone from the UI.")) return;
    try { await api.delete("/users/me"); logout(); nav("/"); }
    catch (e) { fail(e?.response?.data?.detail || "Failed"); }
  };
  const copyCode = () => {
    navigator.clipboard.writeText(user.user_code);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>Account</div>
      <h1 className="sc-heading text-4xl font-black tracking-tight mt-1 mb-8">Your Settings</h1>

      <div className="grid lg:grid-cols-12 gap-4">
        {/* Sidebar */}
        <aside className="lg:col-span-3 sc-card p-3 h-fit">
          <div className="flex items-center gap-3 p-3 mb-2 border-b border-white/10 pb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center sc-heading font-bold" style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)" }}>
              {user.username[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold truncate">{user.username}</div>
              <div className="text-[10px] sc-mono text-zinc-500 truncate">{user.user_code}</div>
            </div>
          </div>
          {TABS.map(t => (
            <button
              key={t.id}
              data-testid={`account-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`w-full text-left px-3 py-2.5 rounded-md flex items-center gap-2 text-sm mb-1 transition ${tab === t.id ? "bg-[color:var(--sc-pink)]/10 text-white border border-[color:var(--sc-pink)]/40" : "text-zinc-400 hover:bg-white/5"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
          <button data-testid="account-signout" onClick={() => { logout(); nav("/"); }} className="w-full text-left px-3 py-2.5 rounded-md flex items-center gap-2 text-sm mt-3 text-[color:var(--sc-pink)] hover:bg-[color:var(--sc-pink)]/10">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </aside>

        {/* Content */}
        <section className="lg:col-span-9 space-y-4">
          {msg && <div className="p-3 border border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981] text-sm rounded-md">{msg}</div>}
          {err && <div className="p-3 border border-[#FF1053]/30 bg-[#FF1053]/10 text-[#FF1053] text-sm rounded-md">{err}</div>}

          {tab === "profile" && (
            <div className="sc-card p-6 space-y-4">
              <div className="sc-heading text-xl font-bold">Profile</div>
              <div>
                <label className="sc-label block mb-2">MystiCast ID</label>
                <div className="flex items-center gap-2">
                  <div className="sc-mono text-lg font-bold tracking-widest">{user.user_code}</div>
                  <button onClick={copyCode} className="p-2 rounded-md border border-white/10 hover:border-[color:var(--sc-volt)] hover:text-[color:var(--sc-volt)]" data-testid="account-copy-code">
                    {copied ? <Check className="w-4 h-4 text-[color:var(--sc-volt)]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="sc-label block mb-2">Email</label>
                <input data-testid="account-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="sc-input" />
              </div>
              <div>
                <label className="sc-label block mb-2">Bio</label>
                <textarea data-testid="account-bio" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="sc-input min-h-[100px]" placeholder="Tell viewers about yourself" />
              </div>
              <button data-testid="account-save-profile" onClick={saveProfile} className="sc-btn">Save Profile</button>
            </div>
          )}

          {tab === "verify" && <KYCCard />}

          {tab === "security" && (
            <div className="space-y-4">
              <div className="sc-card p-6 space-y-4">
                <div className="sc-heading text-xl font-bold">Password</div>
                <p className="text-xs text-zinc-400">Change your password. Use at least 6 characters — longer + mixed case + numbers is stronger.</p>
                <div>
                  <label className="sc-label block mb-2">Current password</label>
                  <input data-testid="account-current-pw" type="password" value={pwd.current_password} onChange={e => setPwd({...pwd, current_password: e.target.value})} className="sc-input" />
                </div>
                <div>
                  <label className="sc-label block mb-2">New password</label>
                  <input data-testid="account-new-pw" type="password" value={pwd.new_password} onChange={e => setPwd({...pwd, new_password: e.target.value})} className="sc-input" />
                </div>
                <button data-testid="account-change-pw" onClick={savePassword} className="sc-btn">Change Password</button>
              </div>
              <TwoFactorSection notify={notify} />
            </div>
          )}

          {tab === "privacy" && (
            <div className="sc-card p-6 space-y-4">
              <div className="sc-heading text-xl font-bold">Privacy</div>
              <div className="flex items-start justify-between gap-4 p-4 border border-white/10 rounded-md">
                <div className="flex-1">
                  <div className="text-sm font-bold">Anonymous mode</div>
                  <p className="text-xs text-zinc-400 mt-1">Hide your real username in chat and lookup. You'll appear as "Anon-{user.user_code?.slice(3)}" to non-friends.</p>
                </div>
                <button
                  data-testid="account-toggle-anon"
                  onClick={toggleAnon}
                  className={`px-4 py-2 rounded-full text-xs font-bold sc-mono ${user.is_anonymous ? "bg-[color:var(--sc-volt)] text-black" : "border border-white/10 text-zinc-400"}`}
                >
                  {user.is_anonymous ? "ON" : "OFF"}
                </button>
              </div>
              <div className="p-4 border border-white/10 rounded-md">
                <div className="text-sm font-bold mb-1">Direct Message policy</div>
                <p className="text-xs text-zinc-400">Anyone can DM you unless blocked. To limit DMs to friends only, block accounts that spam you or use the auto-mod panel on your streamer dashboard.</p>
              </div>
            </div>
          )}

          {tab === "blocks" && (
            <div className="sc-card p-6 space-y-4">
              <div className="sc-heading text-xl font-bold">Blocks &amp; Mutes</div>

              <div>
                <div className="sc-label mb-2 flex items-center gap-2"><Ban className="w-3 h-3 text-[color:var(--sc-pink)]" /> Blocked ({social.blocks.length})</div>
                {social.blocks.length === 0 && <div className="text-xs text-zinc-500 py-2">No blocked users.</div>}
                {social.blocks.map(b => (
                  <div key={b.blocked_user_id} className="flex items-center justify-between p-2 border border-white/10 rounded-sm mb-2">
                    <div className="text-sm">{b.blocked_username}</div>
                    <button data-testid={`account-unblock-${b.blocked_username}`} onClick={() => social.unblockUser(b.blocked_username)} className="sc-btn-ghost text-xs">Unblock</button>
                  </div>
                ))}
              </div>

              <div>
                <div className="sc-label mb-2 flex items-center gap-2"><VolumeX className="w-3 h-3 text-[color:var(--sc-orange)]" /> Muted ({social.mutes.length})</div>
                {social.mutes.length === 0 && <div className="text-xs text-zinc-500 py-2">No muted users.</div>}
                {social.mutes.map(m => (
                  <div key={m.muted_user_id} className="flex items-center justify-between p-2 border border-white/10 rounded-sm mb-2">
                    <div className="text-sm">{m.muted_username}</div>
                    <button data-testid={`account-unmute-${m.muted_username}`} onClick={() => social.unmuteUser(m.muted_username)} className="sc-btn-ghost text-xs">Unmute</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "plans" && (
            <div className="sc-card p-6 space-y-4" data-testid="account-plans-tab">
              <div className="sc-heading text-xl font-bold">Your Plan</div>
              <div className="p-4 border rounded-md" style={{
                borderColor: user.tier === "premium" ? "#00E5FF" : user.tier === "plus" ? "#FFD700" : "#C08457",
                background: user.tier === "premium" ? "rgba(0,229,255,0.05)" : user.tier === "plus" ? "rgba(255,215,0,0.05)" : "rgba(192,132,87,0.05)",
              }}>
                <div className="sc-label" style={{ color: user.tier === "premium" ? "#00E5FF" : user.tier === "plus" ? "#FFD700" : "#C08457" }}>
                  Current tier
                </div>
                <div className="sc-heading text-3xl font-black uppercase mt-1" style={{ color: user.tier === "premium" ? "#00E5FF" : user.tier === "plus" ? "#FFD700" : "#C08457" }}>
                  {user.tier === "premium" ? "DIAMOND" : user.tier === "plus" ? "GOLD" : "BRONZE"}
                </div>
                <div className="text-xs text-zinc-400 mt-2">
                  {user.tier === "free" ? "Compare plans and upgrade for cosmetics + creator perks." : "You're subscribed. Enjoy the perks!"}
                </div>
              </div>
              <div className="flex gap-2">
                <Link to="/account/plans" className="sc-btn text-xs" data-testid="account-plans-cta">Compare Plans</Link>
                <Link to="/credits" className="sc-btn-ghost text-xs">Coin History</Link>
              </div>
            </div>
          )}

          {tab === "account" && (
            <div className="space-y-4">
              <div className="sc-card p-6 space-y-3">
                <div className="sc-heading text-xl font-bold">Account Info</div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="sc-label block mb-1">Username</span> {user.username}</div>
                  <div><span className="sc-label block mb-1">Gold Coins</span> <span className="text-[#FFD700] font-bold">{user.gold_coins ?? 0}</span> <span className="text-[10px] text-zinc-500">(redeemable)</span></div>
                  <div><span className="sc-label block mb-1">Sparks</span> <span className="text-[#00E5FF] font-bold">{user.sparks ?? user.credits ?? 0}</span> <span className="text-[10px] text-zinc-500">(free play)</span></div>
                  <div><span className="sc-label block mb-1">Wins</span> {user.wins}</div>
                  <div><span className="sc-label block mb-1">Role</span> {user.is_admin ? "Admin" : (user.is_verified_streamer ? "Verified Streamer" : "User")}</div>
                  <div><span className="sc-label block mb-1">KYC</span> {user.kyc_status || "not_started"}</div>
                </div>
              </div>
              <div className="sc-card p-6 border border-[color:var(--sc-pink)]/30">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-[color:var(--sc-pink)]" />
                  <div className="sc-heading text-xl font-bold">Danger Zone</div>
                </div>
                <p className="text-xs text-zinc-400 mb-4">Deactivating your account signs you out and prevents future login. Your channel will be taken offline. This action requires admin intervention to reverse.</p>
                <button data-testid="account-delete" onClick={deleteAccount} className="sc-btn text-xs" style={{ background: "#FF1053" }}>Deactivate Account</button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ============================================================
// 2FA (TOTP) enroll / disable / recovery-codes UI
// ============================================================
function TwoFactorSection({ notify }) {
  const [status, setStatus] = useState({ enabled: false, has_pending: false });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null); // { otpauth_uri, qr_data_url, secret }
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const refresh = async () => {
    try { const r = await api.get("/2fa/status"); setStatus(r.data); } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    if (!password) return notify("Enter your password first");
    setBusy(true);
    try {
      const r = await api.post("/2fa/setup", { password });
      setSetup(r.data);
      notify("Scan the QR in your authenticator app");
    } catch (e) {
      notify(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code) return notify("Enter the 6-digit code");
    setBusy(true);
    try {
      const r = await api.post("/2fa/verify", { code });
      setRecoveryCodes(r.data.recovery_codes);
      setSetup(null); setCode(""); setPassword("");
      notify("2FA enabled");
      refresh();
    } catch (e) {
      notify(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!password || !code) return notify("Password + 2FA/recovery code required");
    setBusy(true);
    try {
      await api.post("/2fa/disable", { password, code });
      notify("2FA disabled");
      setShowDisable(false); setPassword(""); setCode("");
      refresh();
    } catch (e) {
      notify(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const regen = async () => {
    if (!password) return notify("Enter your password to regenerate");
    setBusy(true);
    try {
      const r = await api.post("/2fa/regenerate-codes", { password });
      setRecoveryCodes(r.data.recovery_codes);
      setPassword("");
      notify("New recovery codes generated");
    } catch (e) {
      notify(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="sc-card p-6 space-y-4" data-testid="two-factor-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="sc-heading text-xl font-bold flex items-center gap-2">
            Two-Factor Authentication (TOTP)
            {status.enabled && <span className="chip-neon volt"><Check className="w-3 h-3" /> ENABLED</span>}
          </div>
          <p className="text-xs text-zinc-400 mt-2">
            Add a second layer of protection. Use Google Authenticator, 1Password, Authy or any RFC-6238 TOTP app.
            When enabled, sign-in will require a 6-digit code.
          </p>
        </div>
      </div>

      {recoveryCodes && (
        <div className="border border-[color:var(--sc-volt)]/50 rounded p-4 bg-[color:var(--sc-volt)]/5" data-testid="recovery-codes-block">
          <div className="sc-label mb-2" style={{ color: "var(--sc-volt)" }}>★ SAVE THESE RECOVERY CODES</div>
          <p className="text-xs text-zinc-400 mb-3">Each code can be used once to sign in without your authenticator. Store them somewhere safe — they will not be shown again.</p>
          <div className="grid grid-cols-2 gap-1 sc-mono text-sm">
            {recoveryCodes.map((c) => (
              <div key={c} className="bg-black/40 px-3 py-2 rounded" data-testid={`recovery-code-${c}`}>{c}</div>
            ))}
          </div>
          <button onClick={() => setRecoveryCodes(null)} className="sc-btn-ghost text-xs mt-3">I've saved them</button>
        </div>
      )}

      {!status.enabled && !setup && (
        <div className="space-y-3">
          <label className="sc-label block">Password</label>
          <input
            type="password"
            className="sc-input"
            placeholder="Enter your password to enroll"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="2fa-setup-password"
          />
          <button onClick={startSetup} disabled={busy || !password} className="sc-btn" data-testid="2fa-setup-start">
            Set up 2FA
          </button>
        </div>
      )}

      {!status.enabled && setup && (
        <div className="space-y-4" data-testid="2fa-setup-panel">
          <div className="text-xs text-zinc-400">Scan this QR code with your authenticator app, then enter the 6-digit code below.</div>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img src={setup.qr_data_url} alt="TOTP QR" className="w-40 h-40 rounded border border-white/10 bg-white p-1" data-testid="2fa-qr"  loading="lazy" decoding="async" />
            <div className="flex-1">
              <div className="sc-label mb-2">Manual key</div>
              <div className="sc-mono text-xs bg-black/40 p-2 rounded break-all mb-3" data-testid="2fa-secret">{setup.secret}</div>
              <label className="sc-label block mb-2">6-digit code from app</label>
              <input
                className="sc-input tracking-widest text-lg"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="2fa-setup-code"
              />
              <div className="flex gap-2 mt-3">
                <button onClick={verify} disabled={busy || code.length !== 6} className="sc-btn" data-testid="2fa-setup-verify">Verify &amp; Enable</button>
                <button onClick={() => setSetup(null)} className="sc-btn-ghost">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {status.enabled && !showDisable && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowDisable(true)} className="sc-btn-ghost text-xs" data-testid="2fa-disable-open">Disable 2FA</button>
            <div className="flex gap-2 items-center">
              <input
                type="password"
                className="sc-input py-1.5 text-xs w-56"
                placeholder="Password to regenerate codes"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="2fa-regen-password"
              />
              <button onClick={regen} disabled={busy || !password} className="sc-btn-ghost text-xs" data-testid="2fa-regen">Regenerate recovery codes</button>
            </div>
          </div>
        </div>
      )}

      {status.enabled && showDisable && (
        <div className="space-y-3 border border-[color:var(--sc-pink)]/40 rounded p-4" data-testid="2fa-disable-panel">
          <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Confirm to disable</div>
          <input type="password" className="sc-input" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="2fa-disable-password" />
          <input className="sc-input" placeholder="6-digit code OR one recovery code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="2fa-disable-code" />
          <div className="flex gap-2">
            <button onClick={disable} disabled={busy} className="sc-btn text-xs" data-testid="2fa-disable-confirm">Disable 2FA</button>
            <button onClick={() => { setShowDisable(false); setPassword(""); setCode(""); }} className="sc-btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
